import React, { forwardRef, useId, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { buildSyntheticChangeEvent, filterInputValue } from './inputUtils';

/**
 * @typedef {'text'|'number'|'email'|'password'|'tel'|'url'|'search'|'date'} InputType
 * @typedef {'text'|'varchar'|'number'|'decimal'|'alphanumeric'} InputRestrict
 */

const BaseInput = forwardRef(function BaseInput(
  {
    id,
    name,
    label,
    type = 'text',
    value = '',
    onChange,
    onBlur,
    placeholder,
    required = false,
    error,
    helperText,
    disabled = false,
    loading = false,
    valid = false,
    restrict,
    startIcon,
    endIcon,
    showPasswordToggle,
    size = 'md',
    className = '',
    wrapperClassName = '',
    inputClassName = '',
    autoComplete,
    maxLength,
    min,
    max,
    step,
    'aria-describedby': ariaDescribedBy,
    groupEnd,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const inputId = id || `ui-input-${generatedId}`;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;
  const isPassword = type === 'password';
  const [passwordVisible, setPasswordVisible] = useState(false);

  const resolvedType = isPassword && (showPasswordToggle ?? true) && passwordVisible ? 'text' : type;
  const isFileInput = type === 'file';

  const stateClass = error
    ? 'ui-input--invalid'
    : valid
      ? 'ui-input--valid'
      : '';

  const sizeClass = size === 'sm' ? 'ui-input--sm' : size === 'lg' ? 'ui-input--lg' : '';

  const handleChange = (e) => {
    if (!onChange) return;
    const raw = e.target.value;
    const filtered = restrict ? filterInputValue(raw, restrict) : raw;
    if (filtered === raw) {
      onChange(e);
      return;
    }
    onChange(buildSyntheticChangeEvent(e.target.name, filtered));
  };

  const describedBy = [
    ariaDescribedBy,
    error ? errorId : null,
    helperText ? helperId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  const inputElement = (
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={resolvedType}
          {...(isFileInput ? {} : { value: value ?? '' })}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled || loading}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          className={[
            'ui-input',
            sizeClass,
            stateClass,
            startIcon ? 'ui-input--has-start-icon' : '',
            endIcon ? 'ui-input--has-end-icon' : '',
            isPassword && (showPasswordToggle ?? true) ? 'ui-input--has-toggle' : '',
            groupEnd ? 'ui-input-group__input' : '',
            loading ? 'ui-input--loading' : '',
            inputClassName,
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />
  );

  return (
    <div className={`ui-field ${wrapperClassName}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="ui-field__label">
          {label}
          {required && <span className="ui-field__required">*</span>}
        </label>
      )}

      <div className={groupEnd ? 'ui-input-group' : 'ui-input-wrap'}>
        {startIcon && (
          <span className="ui-input__icon ui-input__icon--start" aria-hidden>
            {startIcon}
          </span>
        )}

        {groupEnd ? (
          <>
            {inputElement}
            <div className="ui-input-group__addon">{groupEnd}</div>
          </>
        ) : (
          <>
        {inputElement}

        {loading && <span className="ui-input__spinner" aria-hidden />}

        {!loading && endIcon && !isPassword && (
          <span className="ui-input__icon ui-input__icon--end" aria-hidden>
            {endIcon}
          </span>
        )}

        {!loading && isPassword && (showPasswordToggle ?? true) && (
          <button
            type="button"
            className="ui-input__toggle"
            onClick={() => setPasswordVisible((v) => !v)}
            aria-label={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            tabIndex={-1}
          >
            {passwordVisible ? <FiEyeOff /> : <FiEye />}
          </button>
        )}
          </>
        )}
      </div>

      <div className="ui-field__error-slot">
        {error ? (
          <span id={errorId} className="ui-field__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>

      {helperText && !error && (
        <p id={helperId} className="ui-field__helper">
          {helperText}
        </p>
      )}
    </div>
  );
});

export default BaseInput;
